import { MIN_SELECTION_PX, Point, Rect, Size, clampRect, normalizeRect } from './support-image-editor.geometry';
import { SNIP_HANDLES, SnipHandle, confirmButtonPosition, moveRect, resizeRect } from './support-snip-geometry';
import { HANDLE_ATTRIBUTE, ROOT_CLASS, STYLE, STYLE_ID } from './support-snip-overlay.styles';

/** Gekozen gebied in viewport-CSS-pixels (layout-viewport, oorsprong linksboven). */
export type SnipRect = Rect;

export type SnipResult = { kind: 'region'; rect: SnipRect } | { kind: 'full' } | { kind: 'cancelled' };

export interface SnipLabels {
  hint: string;
  /** Hint zodra er een kader staat en de gebruiker het kan bijstellen. */
  adjustHint: string;
  busy: string;
  /** Bezig-tekst voor de DOM-render nadat het native pad geannuleerd of mislukt is. */
  busyFallback: string;
  /** Bezig-tekst zolang de browser-deeldialoog (native tab-capture) openstaat. */
  shareHint: string;
  /** Tekst op de bevestigknop bij het kader. */
  confirm: string;
  cancel: string;
}

/** Markeert het overlay-element zodat de screenshot-filter het weglaat. */
export const SNIP_OVERLAY_ATTRIBUTE = 'data-support-overlay';

const CONFIRM_MARGIN_PX = 8;

/** Lopende pointer-interactie: kader trekken, een zijde slepen of het kader verschuiven. */
type Drag =
  | { kind: 'select'; start: Point }
  | { kind: 'resize'; handle: SnipHandle; origin: Point; rect: Rect }
  | { kind: 'move'; origin: Point; rect: Rect };

/**
 * Snipping-tool-achtige laag over het hele venster: slepen trekt een kader dat
 * daarna met handles bij te stellen of te verschuiven is; de bevestigknop of
 * Enter legt het vast. Enter (of dubbelklik) zonder kader neemt de hele
 * viewport, Escape annuleert. Wordt imperatief aan `document.body` gehangen,
 * zodat hij ook werkt terwijl het support-paneel dicht is en niets in de
 * Angular-boom hoeft.
 */
export class SupportSnipOverlay {
  private readonly root: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly hintLabel: HTMLSpanElement;
  private readonly cancel: HTMLButtonElement;
  private readonly confirm: HTMLButtonElement;
  private readonly selection: HTMLDivElement;
  private readonly size: HTMLSpanElement;
  private readonly busy: HTMLDivElement;
  /** Permanent aanwezig, zodat hulptechnologie de statuswissel naar bezig meldt. */
  private readonly live: HTMLSpanElement;
  /** Tekstlabel in de bezig-pil; setBusy(text) wisselt het. */
  private readonly busyLabel: HTMLSpanElement;
  private readonly labels: SnipLabels;
  /** visibility vóór hide(), zodat show() exact herstelt. */
  private visibilityBeforeHide?: string;

  /** Het staande kader in de bijstelfase; undefined zolang er geen kader is. */
  private rect?: Rect;
  private drag?: Drag;
  private pointerId?: number;
  private resolve?: (result: SnipResult) => void;
  private previousFocus?: Element;
  private listening = false;
  private destroyed = false;

  constructor(
    private readonly doc: Document,
    labels: SnipLabels,
  ) {
    this.labels = labels;
    this.root = doc.createElement('div');
    this.root.className = ROOT_CLASS;
    this.root.setAttribute(SNIP_OVERLAY_ATTRIBUTE, '');
    this.root.tabIndex = -1;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', labels.hint);
    this.root.addEventListener('contextmenu', this.onContextMenu);

    this.hint = this.pill(`${ROOT_CLASS}-hint`, labels.hint);
    this.hintLabel = this.hint.firstElementChild as HTMLSpanElement;
    this.cancel = doc.createElement('button');
    this.cancel.type = 'button';
    this.cancel.className = `${ROOT_CLASS}-cancel`;
    this.cancel.textContent = labels.cancel;
    this.cancel.addEventListener('click', this.onCancelClick);
    this.hint.appendChild(this.cancel);

    this.selection = doc.createElement('div');
    this.selection.className = `${ROOT_CLASS}-selection`;
    this.selection.hidden = true;
    this.size = doc.createElement('span');
    this.size.className = `${ROOT_CLASS}-size`;
    this.selection.appendChild(this.size);
    for (const handle of SNIP_HANDLES) {
      const element = doc.createElement('div');
      element.className = `${ROOT_CLASS}-handle`;
      element.setAttribute(HANDLE_ATTRIBUTE, handle);
      this.selection.appendChild(element);
    }

    this.confirm = doc.createElement('button');
    this.confirm.type = 'button';
    this.confirm.className = `${ROOT_CLASS}-confirm`;
    this.confirm.hidden = true;
    const icon = doc.createElement('i');
    icon.className = 'fas fa-camera';
    icon.setAttribute('aria-hidden', 'true');
    const confirmLabel = doc.createElement('span');
    confirmLabel.textContent = labels.confirm;
    this.confirm.append(icon, confirmLabel);
    this.confirm.addEventListener('click', this.onConfirmClick);
    this.confirm.addEventListener('pointerdown', this.stopPropagation);

    this.busy = this.pill(`${ROOT_CLASS}-busy`, labels.busy);
    this.busyLabel = this.busy.firstElementChild as HTMLSpanElement;
    const spinner = doc.createElement('span');
    spinner.className = `${ROOT_CLASS}-spinner`;
    this.busy.prepend(spinner);
    this.busy.hidden = true;
    // De live-regio meldt de bezig-tekst al; de zichtbare pil niet nog eens laten voorlezen.
    this.busy.setAttribute('aria-hidden', 'true');

    this.live = doc.createElement('span');
    this.live.className = `${ROOT_CLASS}-live`;
    this.live.setAttribute('aria-live', 'polite');

    this.root.append(this.hint, this.selection, this.confirm, this.busy, this.live);
  }

  /** Toont de overlay; resolvet bij bevestigen van een kader, Enter/dubbelklik of Escape. */
  select(): Promise<SnipResult> {
    if (this.destroyed) return Promise.resolve({ kind: 'cancelled' });
    this.ensureStyle();
    this.previousFocus = this.doc.activeElement ?? undefined;
    this.doc.body.appendChild(this.root);
    this.root.focus({ preventScroll: true });
    this.listen();
    return new Promise(resolve => {
      this.resolve = resolve;
    });
  }

  /**
   * Bezig-status tijdens de capture: kader en dimlaag blijven, invoer staat uit.
   * De laag blijft pointer-events vangen zodat de pagina eronder niet bedienbaar is.
   * `text` vervangt de standaard bezig-tekst (bv. de hint bij de deeldialoog);
   * mag herhaald aangeroepen worden om de tekst te wisselen.
   */
  setBusy(text: string = this.labels.busy): void {
    if (this.destroyed) return;
    this.unlisten();
    this.endDrag();
    this.leaveAdjust();
    this.root.classList.add('is-busy');
    this.hint.hidden = true;
    this.busy.hidden = false;
    this.busyLabel.textContent = text;
    this.live.textContent = text;
  }

  /**
   * Maakt de hele laag (dim, kader, pil) onzichtbaar zonder hem uit de DOM te
   * halen, zodat een native tab-capture de pagina zonder overlay ziet. De
   * aanroeper wacht minstens één frame voor hij het beeld grijpt.
   */
  hide(): void {
    if (this.destroyed || this.visibilityBeforeHide !== undefined) return;
    this.visibilityBeforeHide = this.root.style.visibility;
    this.root.style.visibility = 'hidden';
  }

  /** Maakt de laag na hide() weer zichtbaar; zonder voorafgaande hide() gebeurt er niets. */
  show(): void {
    if (this.destroyed || this.visibilityBeforeHide === undefined) return;
    this.root.style.visibility = this.visibilityBeforeHide;
    this.visibilityBeforeHide = undefined;
  }

  /** Verwijdert DOM en listeners en geeft de focus terug; mag meermaals aangeroepen worden. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unlisten();
    this.root.removeEventListener('contextmenu', this.onContextMenu);
    this.root.remove();
    this.restoreFocus();
    this.settle({ kind: 'cancelled' });
  }

  private listen(): void {
    if (this.listening) return;
    this.listening = true;
    this.root.addEventListener('pointerdown', this.onPointerDown);
    this.root.addEventListener('pointermove', this.onPointerMove);
    this.root.addEventListener('pointerup', this.onPointerUp);
    this.root.addEventListener('pointercancel', this.onPointerCancel);
    this.root.addEventListener('dblclick', this.onDoubleClick);
    this.doc.addEventListener('keydown', this.onKeyDown, true);
  }

  private unlisten(): void {
    if (!this.listening) return;
    this.listening = false;
    this.root.removeEventListener('pointerdown', this.onPointerDown);
    this.root.removeEventListener('pointermove', this.onPointerMove);
    this.root.removeEventListener('pointerup', this.onPointerUp);
    this.root.removeEventListener('pointercancel', this.onPointerCancel);
    this.root.removeEventListener('dblclick', this.onDoubleClick);
    this.doc.removeEventListener('keydown', this.onKeyDown, true);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    // De hint-pil laat pointer-events door; alleen de knoppen zelf zijn geen startpunt.
    if (this.isOnButton(event)) return;
    event.preventDefault();
    const point = { x: event.clientX, y: event.clientY };
    const handle = this.handleAt(event);
    if (this.rect && handle) this.beginResize(handle, point, this.rect);
    else if (this.rect && this.isOnSelection(event)) this.beginMove(point, this.rect);
    else this.beginSelect(point);
    this.pointerId = event.pointerId;
    this.capturePointer(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.isDragging(event)) return;
    this.root.classList.add('is-dragging');
    this.showSelection(this.applyDrag(event));
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.isDragging(event)) return;
    const rect = this.applyDrag(event);
    const wasSelect = this.drag?.kind === 'select';
    this.endDrag();
    if (wasSelect && (rect.width < MIN_SELECTION_PX || rect.height < MIN_SELECTION_PX)) {
      // Een klik zonder slepen: de hint blijft, er gebeurt niets.
      this.hideSelection();
      return;
    }
    this.rect = rect;
    this.enterAdjust();
    this.showSelection(rect);
    this.confirm.focus({ preventScroll: true });
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (!this.isDragging(event)) return;
    const drag = this.drag;
    this.endDrag();
    if (!drag || drag.kind === 'select') {
      this.hideSelection();
      return;
    }
    // Bijstellen afgebroken: het kader van vóór de sleep komt terug.
    this.rect = drag.rect;
    this.showSelection(drag.rect);
  };

  private readonly onDoubleClick = (event: MouseEvent): void => {
    if (this.isOnButton(event)) return;
    event.preventDefault();
    this.finish(this.rect ? { kind: 'region', rect: this.rect } : { kind: 'full' });
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' && event.key !== 'Enter') return;
    // Vóór de app: het paneel en dialogen luisteren ook op Escape.
    event.preventDefault();
    event.stopPropagation();
    // Enter op de gefocuste annuleerknop activeert die knop, niet "hele scherm".
    if (event.key === 'Escape' || this.isOnCancel(event)) {
      this.finish({ kind: 'cancelled' });
      return;
    }
    this.finish(this.rect ? { kind: 'region', rect: this.rect } : { kind: 'full' });
  };

  private readonly onCancelClick = (): void => {
    this.finish({ kind: 'cancelled' });
  };

  private readonly onConfirmClick = (): void => {
    if (!this.listening || !this.rect) return;
    this.finish({ kind: 'region', rect: this.rect });
  };

  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  private readonly stopPropagation = (event: Event): void => {
    event.stopPropagation();
  };

  /** Na een beslissing luistert de overlay niet meer; de aanroeper zet hem op bezig of ruimt op. */
  private finish(result: SnipResult): void {
    this.endDrag();
    this.unlisten();
    this.settle(result);
  }

  private settle(result: SnipResult): void {
    const resolve = this.resolve;
    this.resolve = undefined;
    resolve?.(result);
  }

  private restoreFocus(): void {
    const previous = this.previousFocus;
    this.previousFocus = undefined;
    if (!(previous instanceof HTMLElement) || !previous.isConnected) return;
    previous.focus({ preventScroll: true });
  }

  private isOnCancel(event: Event): boolean {
    return event.target instanceof Node && this.cancel.contains(event.target);
  }

  private isOnButton(event: Event): boolean {
    return this.isOnCancel(event) || (event.target instanceof Node && this.confirm.contains(event.target));
  }

  private isOnSelection(event: Event): boolean {
    return event.target instanceof Node && this.selection.contains(event.target);
  }

  private handleAt(event: Event): SnipHandle | undefined {
    if (!(event.target instanceof Element)) return undefined;
    const handle = event.target.closest(`[${HANDLE_ATTRIBUTE}]`)?.getAttribute(HANDLE_ATTRIBUTE);
    return SNIP_HANDLES.find(candidate => candidate === handle);
  }

  private isDragging(event: PointerEvent): boolean {
    return this.drag !== undefined && event.pointerId === this.pointerId;
  }

  /** Nieuw kader trekken; een staand kader verdwijnt meteen. */
  private beginSelect(start: Point): void {
    this.rect = undefined;
    this.leaveAdjust();
    this.hideSelection();
    this.root.focus({ preventScroll: true });
    this.drag = { kind: 'select', start };
  }

  private beginResize(handle: SnipHandle, origin: Point, rect: Rect): void {
    this.drag = { kind: 'resize', handle, origin, rect };
  }

  private beginMove(origin: Point, rect: Rect): void {
    this.drag = { kind: 'move', origin, rect };
  }

  /** Kader dat bij de huidige pointerpositie hoort, gegeven de lopende sleep. */
  private applyDrag(event: PointerEvent): Rect {
    const point = { x: event.clientX, y: event.clientY };
    const bounds = this.viewport();
    const drag = this.drag;
    if (!drag) return clampRect(normalizeRect(point, point), bounds);
    switch (drag.kind) {
      case 'select':
        return clampRect(normalizeRect(drag.start, point), bounds);
      case 'resize':
        return resizeRect(drag.rect, drag.handle, point.x - drag.origin.x, point.y - drag.origin.y, bounds);
      case 'move':
        return moveRect(drag.rect, point.x - drag.origin.x, point.y - drag.origin.y, bounds);
    }
  }

  private endDrag(): void {
    if (this.pointerId !== undefined) this.releasePointer(this.pointerId);
    this.drag = undefined;
    this.pointerId = undefined;
    this.root.classList.remove('is-dragging');
  }

  /** Bijstelfase: handles en bevestigknop zichtbaar, hint wisselt naar de bijstel-uitleg. */
  private enterAdjust(): void {
    this.root.classList.add('is-adjusting');
    this.hintLabel.textContent = this.labels.adjustHint;
    this.confirm.hidden = false;
  }

  private leaveAdjust(): void {
    this.root.classList.remove('is-adjusting');
    this.hintLabel.textContent = this.labels.hint;
    this.confirm.hidden = true;
  }

  /** Layout-viewport (zonder eventuele vensterscrollbar), dezelfde maat als de capture gebruikt. */
  private viewport(): Size {
    return {
      width: this.doc.documentElement.clientWidth,
      height: this.doc.documentElement.clientHeight,
    };
  }

  private showSelection(rect: Rect): void {
    this.selection.hidden = false;
    this.root.classList.add('has-selection');
    this.selection.style.left = `${rect.x}px`;
    this.selection.style.top = `${rect.y}px`;
    this.selection.style.width = `${rect.width}px`;
    this.selection.style.height = `${rect.height}px`;
    this.size.textContent = `${Math.round(rect.width)} × ${Math.round(rect.height)}`;
    if (!this.confirm.hidden) this.placeConfirm(rect);
  }

  private placeConfirm(rect: Rect): void {
    const button = { width: this.confirm.offsetWidth, height: this.confirm.offsetHeight };
    const position = confirmButtonPosition(rect, this.viewport(), button, CONFIRM_MARGIN_PX);
    this.confirm.style.left = `${position.x}px`;
    this.confirm.style.top = `${position.y}px`;
    this.confirm.classList.toggle('is-inside', position.inside);
  }

  private hideSelection(): void {
    this.selection.hidden = true;
    this.root.classList.remove('has-selection');
  }

  private capturePointer(pointerId: number): void {
    try {
      this.root.setPointerCapture(pointerId);
    } catch {
      // Synthetische events (specs) hebben geen actieve pointer; slepen werkt dan zonder capture.
    }
  }

  private releasePointer(pointerId: number): void {
    try {
      if (this.root.hasPointerCapture(pointerId)) this.root.releasePointerCapture(pointerId);
    } catch {
      // Zie capturePointer.
    }
  }

  private pill(className: string, text: string): HTMLDivElement {
    const pill = this.doc.createElement('div');
    pill.className = `${ROOT_CLASS}-pill ${className}`;
    const label = this.doc.createElement('span');
    label.textContent = text;
    pill.appendChild(label);
    return pill;
  }

  private ensureStyle(): void {
    if (this.doc.getElementById(STYLE_ID)) return;
    const style = this.doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE;
    this.doc.head.appendChild(style);
  }
}
